import React, { useEffect, useState, useCallback, useRef } from 'react';
import { MapPin, Camera, X, Shield } from '@phosphor-icons/react';
import { Button } from './ui/button';

/**
 * PermissionRationaleModal — Play Store-compliant pre-prompt that explains
 * WHY a sensitive permission is being requested *before* the OS prompt fires.
 *
 * Required by Google Play's runtime-permission policy and helpful for trust.
 *
 * Usage: import the singleton helper `requestPermissionWithRationale(kind, opts)`.
 *
 *   await requestPermissionWithRationale('location', { purpose: 'set venue' });
 *   if (granted) { navigator.geolocation.getCurrentPosition(...) }
 *
 * The modal is shown once per `(kind, purpose)` pair per device (localStorage),
 * then suppressed thereafter to avoid nagging.
 */

const STORAGE_KEY = 'beeweddy_permission_rationale_v1';
const RATIONALE_COPY = {
  location: {
    icon: MapPin,
    title: 'We need your device location',
    body: 'BeeWeddy uses your precise location only when you explicitly tap "Use my location" — for setting a venue (captains) or to verify your check-in is at the event site (professionals). Your location is never collected in the background, never shared with third parties, and is only sent to BeeWeddy servers when you take an action.',
    next: 'Continue',
  },
  camera: {
    icon: Camera,
    title: 'We need access to your photos',
    body: 'BeeWeddy uploads only the photos you select for your work gallery, custom themes, or vendor portfolio. We do not scan your library or upload anything in the background. Photos are stored on our servers and shown publicly on your vendor profile.',
    next: 'Choose photo',
  },
};

let pendingResolver = null;
let setOpenExternal = null;

export const requestPermissionWithRationale = (kind, opts = {}) => {
  const purpose = opts.purpose || 'default';
  const storeKey = `${kind}:${purpose}`;
  let memory = {};
  try {
    memory = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
  } catch { memory = {}; }
  if (memory[storeKey]) {
    // Already accepted this rationale before — just resolve true (caller will prompt OS itself).
    return Promise.resolve(true);
  }
  return new Promise((resolve) => {
    pendingResolver = (ok) => {
      if (ok) {
        memory[storeKey] = Date.now();
        try { localStorage.setItem(STORAGE_KEY, JSON.stringify(memory)); } catch {}
      }
      resolve(ok);
      pendingResolver = null;
    };
    if (setOpenExternal) setOpenExternal({ kind, purpose });
  });
};

export const PermissionRationaleModal = () => {
  const [state, setState] = useState(null); // { kind, purpose } | null
  const triggerRef = useRef(null);

  useEffect(() => {
    setOpenExternal = setState;
    return () => { setOpenExternal = null; };
  }, []);

  // Esc to cancel
  useEffect(() => {
    if (!state) return;
    const onKey = (e) => { if (e.key === 'Escape') handle(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const handle = useCallback((ok) => {
    setState(null);
    if (pendingResolver) pendingResolver(ok);
  }, []);

  if (!state) return null;
  const copy = RATIONALE_COPY[state.kind] || RATIONALE_COPY.location;
  const Icon = copy.icon;
  return (
    <div
      className="fixed inset-0 z-[90] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-3 sm:p-4"
      onClick={(e) => { if (e.target === e.currentTarget) handle(false); }}
      data-testid="permission-rationale-overlay"
    >
      <div className="bg-white max-w-md w-full border border-[#E8E1D5] shadow-2xl rounded-t-2xl sm:rounded-lg" data-testid="permission-rationale-modal">
        <div className="px-5 py-4 border-b border-[#E8E1D5] flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Shield size={22} weight="duotone" className="text-brand-primary" />
            <h3 className="font-heading text-xl font-medium">Permission Required</h3>
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={() => handle(false)}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-sand-100 text-text-secondary"
            data-testid="permission-close"
          >
            <X size={16} />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-full bg-brand-primary/10 flex items-center justify-center flex-shrink-0">
              <Icon size={26} weight="duotone" className="text-brand-primary" />
            </div>
            <div>
              <h4 className="font-heading text-lg font-medium mb-1">{copy.title}</h4>
              <p className="text-sm text-text-secondary leading-relaxed">{copy.body}</p>
            </div>
          </div>
          <div className="text-[11px] tracking-wider text-text-secondary bg-sand-50 border border-[#E8E1D5] rounded p-3 leading-relaxed">
            <strong className="text-text-primary">Privacy:</strong> See our{' '}
            <a href="/privacy-policy" target="_blank" rel="noreferrer" className="underline hover:text-brand-primary">Privacy Policy</a>
            {' '}for full details on how we handle this data. You can revoke access anytime in your device settings.
          </div>
          <div className="flex gap-2 pt-1">
            <Button
              ref={triggerRef}
              onClick={() => handle(true)}
              className="flex-1 bg-brand-primary hover:opacity-90 text-white rounded-none"
              data-testid="permission-allow-btn"
            >
              {copy.next}
            </Button>
            <Button
              onClick={() => handle(false)}
              variant="outline"
              className="rounded-none border-[#E8E1D5]"
              data-testid="permission-deny-btn"
            >
              Not now
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PermissionRationaleModal;
